/* eslint-disable camelcase */
import { useState, useEffect } from 'react';
import { graphql, useMutation } from 'react-relay';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements, CardElement, IbanElement, useStripe, useElements,
} from '@stripe/react-stripe-js';
import { LayoutCard } from '@/core/dynamic';
import { useTranslation } from '@/helpers/translate';
import { useZusUserConnectedStore } from '@/helpers/user-auth.js';
import Loading from '@/core/loading';
import HofPlusIcon from '@/components/icons/hofplus';
import miniQLWS from '@/helpers/miniQueryLoaderv3.js';
import {
  mutationCardSetup, mutationClientSecret, mutationChangeCard, mutationResume,
  mutationStartSubscription, mutationCancelSubscription, mutationDeletePm,
} from './graphql/PaymentCorpo';

const querySubscription = graphql`
  query legendCardQuery($corpoRef: String!) {
    payment {
      corpo {
        res: getSubscription(corpoRef: $corpoRef)
        plans: getPlans
      }
    }
  }
`;

const CARD_OPTIONS = {
  style: {
    base: { color: '#32325d', fontSize: '16px', '::placeholder': { color: '#aab7c4' } },
    invalid: { color: '#fa755a', iconColor: '#fa755a' },
  },
};
const IBAN_OPTIONS = { ...CARD_OPTIONS, supportedCountries: ['SEPA'], placeholderCountry: 'FR' };

// `chip` = pastille de statut dans l'en-tete de la carte d'abonnement (fond rouge teinte, cf. le
// teaser Legend+ du header) : rouge pour un abonnement qui tourne, encre attenuee sinon.
const STATUS = {
  active: { label: 'Actif', cls: 'text-green-600', chip: 'text-red bg-red/15' },
  failed: { label: 'Paiement en échec', cls: 'text-red', chip: 'text-white bg-red' },
  canceled: { label: 'Résilié', cls: 'text-black-light', chip: 'text-was-slate bg-was-ink/10' },
  pending: { label: 'En attente', cls: 'text-orange-500', chip: 'text-was-slate bg-was-ink/10' },
  none: { label: 'Aucun abonnement', cls: 'text-black-light', chip: 'text-was-slate bg-was-ink/10' },
};

const INVOICE_STATUS = {
  paid: '✅ Payé',
  open: '⏳ En attente',
  draft: '📝 Brouillon',
  uncollectible: '⛔️ Irrécouvrable',
  void: '❌ Annulé',
};

// Sélecteur de moyen de paiement partagé (souscription, changement, reprise) : deux tuiles
// Carte / SEPA à la place des radios natifs, champ Stripe encadré avec focus visible, mention du
// mandat SEPA (exigée par Stripe) quand l'IBAN est choisi.
const CardGlyph = () => (
  <svg viewBox="0 0 24 24" className="size-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2.5" />
    <path d="M3 10h18" />
    <path d="M7 15h4" strokeLinecap="round" />
  </svg>
);
const BankGlyph = () => (
  <svg viewBox="0 0 24 24" className="size-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M3 10l9-6 9 6" strokeLinejoin="round" />
    <path d="M5 10v8M10 10v8M14 10v8M19 10v8" strokeLinecap="round" />
    <path d="M3 18h18" strokeLinecap="round" />
  </svg>
);
const PAYMENT_TYPES = [
  { value: 'card', label: 'Carte bancaire', field: 'Numéro de carte', Glyph: CardGlyph },
  { value: 'sepa', label: 'Prélèvement SEPA', field: 'IBAN', Glyph: BankGlyph },
];

function PaymentMethodFields({ type, setType }) {
  const current = PAYMENT_TYPES.find((pt) => pt.value === type) || PAYMENT_TYPES[0];
  return (
    <div className="flex flex-col">
      <div className="grid grid-cols-2 gap-2" role="radiogroup">
        {PAYMENT_TYPES.map(({ value, label, Glyph }) => {
          const selected = type === value;
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => setType(value)}
              className={`flex gap-2.5 items-center px-3 py-2.5 text-sm text-left bg-white rounded-lg border transition-colors ${
                selected
                  ? 'border-was-ink ring-1 ring-was-ink text-was-ink'
                  : 'border-was-ink/10 text-was-slate hover:border-was-ink/40 hover:text-was-ink'
              }`}
            >
              <Glyph />
              <span className="font-medium">{label}</span>
            </button>
          );
        })}
      </div>
      <span className="block mt-3 text-xs font-medium text-was-muted">{current.field}</span>
      <div className="px-3 py-3 mt-1 w-full bg-white rounded-lg border transition-colors border-was-ink/15 focus-within:border-was-ink focus-within:ring-1 focus-within:ring-was-ink">
        {type === 'card'
          ? <CardElement options={CARD_OPTIONS} />
          : <IbanElement options={IBAN_OPTIONS} />}
      </div>
      {type === 'sepa' && (
        <p className="mt-2 text-[11px] leading-snug text-was-muted">
          En fournissant votre IBAN, vous autorisez We Are Scene et Stripe, notre prestataire de
          paiement, à envoyer des instructions à votre banque pour débiter votre compte conformément
          à ces instructions.
        </p>
      )}
      <p className="mt-2 text-[11px] text-was-muted">Paiement sécurisé par Stripe.</p>
    </div>
  );
}

function fmtDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR');
}

function fmtMonth(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
}

// Human label for a Stripe coupon (discount), e.g. "-15% pendant 3 mois".
const LOCALE_BY_LANG = { fr: 'fr-FR', en: 'en-GB', es: 'es-ES' };

function fmtMoney(amount, currency, lang = 'fr') {
  if (amount == null) return '';
  const locale = LOCALE_BY_LANG[lang] || LOCALE_BY_LANG.fr;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: (currency || 'eur').toUpperCase(),
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (err) {
    return `${amount} ${(currency || 'eur').toUpperCase()}`;
  }
}

/**
 * Savings of a committed plan vs the no-commitment one (the reference price).
 * Everything is derived from the live Stripe plans — nothing hardcoded.
 * Returns null when the plan has no commitment or there is nothing to compare to.
 */
function offerSavings(plan, plans) {
  if (!plan?.engaged) return null;
  const reference = (plans || []).find((p) => !p.engaged);
  const referenceAmount = reference?.amount;
  if (!referenceAmount || !plan.amount || referenceAmount <= plan.amount) return null;
  const months = plan.commitmentMonths || 12;
  return {
    percent: Math.round((1 - (plan.amount / referenceAmount)) * 100),
    months,
    referenceAmount,
    total: Math.round(plan.amount * months * 100) / 100,
    referenceTotal: Math.round(referenceAmount * months * 100) / 100,
  };
}

function couponLabel(c) {
  if (!c) return null;
  let off = '';
  if (c.percentOff) off = `-${c.percentOff}%`;
  else if (c.amountOff) off = `-${c.amountOff} ${(c.currency || 'eur').toUpperCase()}`;
  let period = '';
  if (c.duration === 'repeating') period = ` pendant ${c.durationInMonths} mois`;
  else if (c.duration === 'once') period = ' (1er mois)';
  else if (c.duration === 'forever') period = ' (à vie)';
  return `${off}${c.name ? ` — ${c.name}` : ''}${period}`;
}

// Change the subscription payment method (card OR SEPA) and retry the failed invoice.
function ChangePaymentBlock({
  corpoRef, corpoName, hasIssue, onChanged, open, onOpen, onClose,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [type, setType] = useState('card');
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [mutSetupCard, cardPending] = useMutation(mutationCardSetup);
  const [mutSetupSepa, sepaPending] = useMutation(mutationClientSecret);
  const [mutChange, changePending] = useMutation(mutationChangeCard);

  const processing = cardPending || sepaPending || changePending || confirming;

  function submit() {
    setError('');
    if (!stripe || !elements) {
      setError('Le module de paiement n\'est pas encore chargé, réessayez.');
      return;
    }
    const isSepa = type === 'sepa';
    const setupMut = isSepa ? mutSetupSepa : mutSetupCard;
    setupMut({
      variables: { corpoRef },
      onCompleted: async (res) => {
        const data = res?.payment?.corpo?.res?.data;
        if (!data?.client_secret) {
          setError('Impossible d\'initialiser le moyen de paiement.');
          return;
        }
        setConfirming(true);
        let result;
        if (isSepa) {
          result = await stripe.confirmSepaDebitSetup(data.client_secret, {
            payment_method: {
              sepa_debit: elements.getElement(IbanElement),
              billing_details: { name: corpoName || corpoRef, email: data.email },
            },
          });
        } else {
          result = await stripe.confirmCardSetup(data.client_secret, {
            payment_method: {
              card: elements.getElement(CardElement),
              billing_details: { name: corpoName || corpoRef },
            },
          });
        }
        if (result.error) {
          setConfirming(false);
          setError(result.error.message || 'Le moyen de paiement a été refusé.');
          return;
        }
        mutChange({
          variables: {
            input: {
              corpoRef, paymentType: isSepa ? 'sepa' : 'card', setupIntentId: result.setupIntent.id, retry: true,
            },
          },
          onCompleted: async (res2) => {
            const payload = res2?.payment?.corpo?.res;
            if (payload?.code !== 200) {
              setConfirming(false);
              setError(payload?.message || 'Le changement de moyen de paiement a échoué.');
              return;
            }
            const clientSecret = payload?.data?.clientSecret;
            if (clientSecret) {
              const confirm = await stripe.confirmCardPayment(clientSecret);
              if (confirm.error) {
                setConfirming(false);
                setError(confirm.error.message || 'Authentification du paiement requise.');
                return;
              }
            }
            setConfirming(false);
            onChanged();
          },
          onError: () => {
            setConfirming(false);
            setError('Le changement de moyen de paiement a échoué.');
          },
        });
      },
      onError: () => setError('Impossible d\'initialiser le moyen de paiement.'),
    });
  }

  if (!open) {
    return (
      <button
        type="button"
        className="flex items-center px-3 py-2 text-white bg-red hover:bg-red-dark"
        onClick={onOpen}
      >
        Changer de moyen de paiement
      </button>
    );
  }

  return (
    <div className="flex flex-col mt-2">
      <div className="lg:max-w-lg">
        <PaymentMethodFields type={type} setType={setType} />
      </div>
      <p className="py-2 text-xs leading-tight text-dark">
        Le nouveau moyen de paiement devient celui de votre abonnement et le dernier prélèvement en échec est immédiatement relancé.
      </p>
      {!error || <div className="mb-2 text-red">{error}</div>}
      {processing ? <Loading /> : (
        <div className="flex gap-2">
          <button
            type="button"
            className="flex items-center justify-center px-3 py-2 text-white lg:h-10 bg-red lg:px-4 hover:bg-red-dark"
            onClick={submit}
          >
            Valider
          </button>
          <button
            type="button"
            className="flex items-center px-3 py-2 gray"
            onClick={() => { onClose(); setError(''); }}
          >
            Annuler
          </button>
        </div>
      )}
    </div>
  );
}

// Resume a subscription scheduled to cancel — same subscription; optionally with a new payment method.
function ResumeBlock({
  corpoRef, corpoName, onResumed, open, onOpen, onClose,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [type, setType] = useState('card');
  const [withNewPm, setWithNewPm] = useState(false);
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [mutSetupCard, cardPending] = useMutation(mutationCardSetup);
  const [mutSetupSepa, sepaPending] = useMutation(mutationClientSecret);
  const [mutResume, resumePending] = useMutation(mutationResume);

  const processing = cardPending || sepaPending || resumePending || confirming;

  function doResume(setupIntentId) {
    mutResume({
      variables: {
        input: setupIntentId
          ? { corpoRef, setupIntentId, paymentType: type }
          : { corpoRef },
      },
      onCompleted: (res) => {
        const payload = res?.payment?.corpo?.res;
        if (payload?.code !== 200) {
          setConfirming(false);
          setError(payload?.message || 'La reprise a échoué.');
          return;
        }
        setConfirming(false);
        onResumed();
      },
      onError: () => { setConfirming(false); setError('La reprise a échoué.'); },
    });
  }

  function submit() {
    setError('');
    // resume keeping the current payment method
    if (!withNewPm) { doResume(null); return; }
    // resume AND switch payment method
    if (!stripe || !elements) { setError('Le module de paiement n\'est pas encore chargé.'); return; }
    const isSepa = type === 'sepa';
    const setupMut = isSepa ? mutSetupSepa : mutSetupCard;
    setupMut({
      variables: { corpoRef },
      onCompleted: async (res) => {
        const data = res?.payment?.corpo?.res?.data;
        if (!data?.client_secret) { setError('Impossible d\'initialiser le moyen de paiement.'); return; }
        setConfirming(true);
        let result;
        if (isSepa) {
          result = await stripe.confirmSepaDebitSetup(data.client_secret, {
            payment_method: {
              sepa_debit: elements.getElement(IbanElement),
              billing_details: { name: corpoName || corpoRef, email: data.email },
            },
          });
        } else {
          result = await stripe.confirmCardSetup(data.client_secret, {
            payment_method: {
              card: elements.getElement(CardElement),
              billing_details: { name: corpoName || corpoRef },
            },
          });
        }
        if (result.error) { setConfirming(false); setError(result.error.message || 'Le moyen de paiement a été refusé.'); return; }
        doResume(result.setupIntent.id);
      },
      onError: () => setError('Impossible d\'initialiser le moyen de paiement.'),
    });
  }

  if (!open) {
    return (
      <button
        type="button"
        className="flex items-center px-3 py-2 text-white bg-green-600 hover:bg-green-700"
        onClick={onOpen}
      >
        Reprendre l&apos;abonnement
      </button>
    );
  }

  return (
    <div className="flex flex-col mt-2">
      <p className="text-xs leading-tight text-dark mb-2">
        Annule la résiliation programmée — même abonnement, aucune interruption.
      </p>
      <label className="flex items-center gap-x-2 cursor-pointer mb-2">
        <input type="checkbox" checked={withNewPm} onChange={(ev) => setWithNewPm(ev.target.checked)} />
        <span>Reprendre avec un nouveau moyen de paiement</span>
      </label>
      {withNewPm && (
        <div className="lg:max-w-lg">
          <PaymentMethodFields type={type} setType={setType} />
        </div>
      )}
      {!error || <div className="my-2 text-red">{error}</div>}
      {processing ? <Loading /> : (
        <div className="flex gap-2 mt-2">
          <button
            type="button"
            className="flex items-center justify-center px-3 py-2 text-white lg:h-10 bg-green-600 lg:px-4 hover:bg-green-700"
            onClick={submit}
          >
            Valider la reprise
          </button>
          <button
            type="button"
            className="flex items-center px-3 py-2 gray"
            onClick={() => { onClose(); setWithNewPm(false); setError(''); }}
          >
            Annuler
          </button>
        </div>
      )}
    </div>
  );
}

// Start a new HOF subscription — pick a plan + a payment method (card OR SEPA). Open to every corpo user.
export function SubscribeBlock({
  corpoRef, corpoName, plans, onSubscribed, assignedCoupon, assignedFreeMonths,
  initialPlanType = '',
}) {
  const { t, lang } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  // No plan preselected: picking one is what reveals the payment form (guided flow).
  // `initialPlanType` lets an entry point that already asked ("choose this plan" on the public
  // hall-of-fame page) open straight on the payment step.
  const [planType, setPlanType] = useState(initialPlanType);
  const [type, setType] = useState('card');
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [mutSetupCard, cardPending] = useMutation(mutationCardSetup);
  const [mutSetupSepa, sepaPending] = useMutation(mutationClientSecret);
  const [mutStart, startPending] = useMutation(mutationStartSubscription);

  const processing = cardPending || sepaPending || startPending || confirming;

  function submit() {
    setError('');
    if (!stripe || !elements) {
      setError('Le module de paiement n\'est pas encore chargé, réessayez.');
      return;
    }
    const isSepa = type === 'sepa';
    const setupMut = isSepa ? mutSetupSepa : mutSetupCard;
    setupMut({
      variables: { corpoRef },
      onCompleted: async (res) => {
        const data = res?.payment?.corpo?.res?.data;
        if (!data?.client_secret) {
          setError('Impossible d\'initialiser le moyen de paiement.');
          return;
        }
        setConfirming(true);
        let result;
        if (isSepa) {
          result = await stripe.confirmSepaDebitSetup(data.client_secret, {
            payment_method: {
              sepa_debit: elements.getElement(IbanElement),
              billing_details: { name: corpoName || corpoRef, email: data.email },
            },
          });
        } else {
          result = await stripe.confirmCardSetup(data.client_secret, {
            payment_method: {
              card: elements.getElement(CardElement),
              billing_details: { name: corpoName || corpoRef },
            },
          });
        }
        if (result.error) {
          setConfirming(false);
          setError(result.error.message || 'Le moyen de paiement a été refusé.');
          return;
        }
        mutStart({
          variables: {
            input: {
              corpoRef, planType, paymentType: isSepa ? 'sepa' : 'card', setupIntentId: result.setupIntent.id,
            },
          },
          onCompleted: async (res2) => {
            const payload = res2?.payment?.corpo?.res;
            if (payload?.code !== 200) {
              setConfirming(false);
              setError(payload?.message || 'La souscription a échoué.');
              return;
            }
            const clientSecret = payload?.data?.clientSecret;
            if (clientSecret) {
              const confirm = await stripe.confirmCardPayment(clientSecret);
              if (confirm.error) {
                setConfirming(false);
                setError(confirm.error.message || 'Authentification du paiement requise.');
                return;
              }
            }
            setConfirming(false);
            onSubscribed();
          },
          onError: () => {
            setConfirming(false);
            setError('La souscription a échoué.');
          },
        });
      },
      onError: () => setError('Impossible d\'initialiser le moyen de paiement.'),
    });
  }

  const hasCoupon = assignedCoupon?.valid;
  const hasFreeMonths = assignedFreeMonths?.valid && assignedFreeMonths?.count > 0;
  // the advantage window closes at the earliest expiry among the valid assignments
  const offerExpiresAt = [
    hasFreeMonths ? assignedFreeMonths.expiresAt : null,
    hasCoupon ? assignedCoupon.expiresAt : null,
  ].filter(Boolean).sort()[0];

  return (
    <div className="flex flex-col mt-2">
      {/* Avantages attribués (remise / mois offerts) si vous souscrivez maintenant */}
      {(hasCoupon || hasFreeMonths) && (
        <div className="p-2 mb-3 text-sm rounded-lg bg-green-50 text-green-700">
          <b>En souscrivant maintenant, vous bénéficiez de :</b>
          <ul className="pl-4 list-disc">
            {hasFreeMonths && (
              <li>
                {assignedFreeMonths.count}
                {' '}
                mois offert
                {assignedFreeMonths.count > 1 ? 's' : ''}
              </li>
            )}
            {hasCoupon && <li>{couponLabel(assignedCoupon)}</li>}
          </ul>
          {offerExpiresAt && (
            <div className="mt-1 text-xs">
              Offre valable jusqu&apos;au
              {' '}
              <b>{fmtDate(offerExpiresAt)}</b>
            </div>
          )}
        </div>
      )}
      {/* Choix de l'offre — deux cartes côte à côte. La carte visée (hover) ou choisie passe en
          "sélectionnée" (bordure encre, fond crème), l'autre s'estompe. Le rouge Legend+ est réservé
          au badge d'économie : les cartes restent neutres pour ne pas saturer la page. Choisir révèle la
          saisie du moyen de paiement. */}
      <div className="grid gap-3 mb-4 sm:grid-cols-2">
        {(plans || []).map((p) => {
          const isSelected = planType === p.planType;
          const isDimmed = !!planType && !isSelected;
          const priceLabel = fmtMoney(p.amount, p.currency, lang);
          const savings = offerSavings(p, plans);
          return (
            <button
              type="button"
              key={p.planType}
              onClick={() => setPlanType(p.planType)}
              className={`flex flex-col gap-2 p-4 text-left rounded-xl border bg-white transition-all duration-200 hover:border-was-ink/40 hover:opacity-100 ${
                isSelected ? 'border-was-ink bg-was-cream' : 'border-was-ink/10'
              } ${isDimmed ? 'opacity-60' : ''}`}
            >
              <div className="flex gap-2 justify-between items-start">
                <span className="text-sm font-medium text-was-ink">
                  {p.engaged
                    ? t('corpo:abonnement.offerEngagedTitle')
                    : t('corpo:abonnement.offerFlexTitle')}
                </span>
                {savings?.percent > 0 && (
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full text-red bg-red/10 shrink-0">
                    {t('corpo:abonnement.offerSavingBadge', { percent: savings.percent })}
                  </span>
                )}
              </div>
              <div className="flex gap-2 items-baseline">
                {savings?.percent > 0 && (
                  <span className="text-sm line-through text-was-muted">
                    {fmtMoney(savings.referenceAmount, p.currency, lang)}
                  </span>
                )}
                <span className="text-3xl font-medium text-was-ink">{priceLabel}</span>
                <span className="text-sm text-was-muted">
                  {t('corpo:abonnement.offerPerMonth')}
                </span>
              </div>
              <p className="text-xs leading-relaxed text-was-slate">
                {savings?.percent > 0
                  ? t('corpo:abonnement.offerEngagedDetail', {
                    total: fmtMoney(savings.total, p.currency, lang),
                    months: savings.months,
                    reference: fmtMoney(savings.referenceTotal, p.currency, lang),
                  })
                  : t('corpo:abonnement.offerFlexDetail')}
              </p>
              <span
                className={`mt-1 px-4 py-2 text-sm font-medium text-center rounded-full transition-colors ${
                  isSelected ? 'text-white bg-was-ink' : 'text-was-ink border border-was-ink/15 hover:bg-was-cream'
                }`}
              >
                {isSelected
                  ? t('corpo:abonnement.offerSelected')
                  : t('corpo:abonnement.offerChoose')}
              </span>
            </button>
          );
        })}
      </div>

      {/* Moyen de paiement — révélé une fois l'offre choisie */}
      {planType && (
        <div className="flex flex-col p-5 rounded-xl border border-was-ink/10 bg-was-cream lg:max-w-lg">
          <h3 className="m-0 text-sm font-medium text-was-ink">
            {t('corpo:abonnement.offerPaymentTitle')}
          </h3>
          <div className="mt-4">
            <PaymentMethodFields type={type} setType={setType} />
          </div>
          {!error || <div className="mt-3 text-sm text-red">{error}</div>}
          {processing ? <div className="mt-4"><Loading /></div> : (
            <button
              type="button"
              className="flex justify-center items-center px-6 py-2.5 mt-4 w-full text-sm font-medium text-white bg-red rounded-full transition-colors sm:w-auto sm:self-start hover:bg-red-dark"
              onClick={submit}
            >
              Souscrire
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Cancel the subscription at period end (reversible via ResumeBlock while the period lasts). Admin-only.
// force:true -> an admin cancellation bypasses the 12-month commitment (same as dedale).
function CancelBlock({
  corpoRef, commitmentEndDate, onCanceled, open, onOpen, onClose,
}) {
  const [error, setError] = useState('');
  const [mutCancel, pending] = useMutation(mutationCancelSubscription);
  const underCommitment = commitmentEndDate && new Date(commitmentEndDate) > new Date();

  function submit() {
    setError('');
    mutCancel({
      variables: { corpoRef, atPeriodEnd: true, force: true },
      onCompleted: (res) => {
        const payload = res?.payment?.corpo?.res;
        if (payload?.code !== 200) {
          setError(payload?.message || 'La résiliation a échoué.');
          return;
        }
        onCanceled();
      },
      onError: () => setError('La résiliation a échoué.'),
    });
  }

  if (!open) {
    return (
      <button
        type="button"
        className="flex items-center px-3 py-2 text-red border border-red hover:text-white hover:bg-red"
        onClick={onOpen}
      >
        Résilier l&apos;abonnement
      </button>
    );
  }

  return (
    <div className="flex flex-col mt-2">
      <p className="text-xs leading-tight text-dark mb-2">
        L&apos;abonnement reste actif jusqu&apos;à la fin de la période déjà payée, puis sera résilié.
        Réversible tant que la période payée n&apos;est pas terminée.
      </p>
      {underCommitment && (
        <div className="p-2 mb-2 text-xs leading-tight rounded-lg bg-gray-100 text-gray-600">
          Vous êtes engagé jusqu&apos;au
          {' '}
          <b>{fmtDate(commitmentEndDate)}</b>
          . En résiliant maintenant, vous sortez avant la fin de votre engagement — une pénalité
          pourra s&apos;appliquer (montant communiqué par votre interlocuteur OfficeRiders).
        </div>
      )}
      {!error || <div className="mb-2 text-red">{error}</div>}
      {pending ? <Loading /> : (
        <div className="flex gap-2">
          <button
            type="button"
            className="flex items-center justify-center px-3 py-2 text-white lg:h-10 bg-red lg:px-4 hover:bg-red/80"
            onClick={submit}
          >
            Confirmer la résiliation
          </button>
          <button
            type="button"
            className="flex items-center px-3 py-2 gray"
            onClick={() => { onClose(); setError(''); }}
          >
            Annuler
          </button>
        </div>
      )}
    </div>
  );
}

// WEA-157: delete the saved subscription payment method (detach from Stripe + clear the dedicated
// subSepaData/subCardData fields). Corpo admin only, and only when no subscription is running
// (backend enforces both). Never touches the booking-payment method (paymentCard screen).
function DeleteBlock({
  corpoRef, onDeleted, open, onOpen, onClose,
}) {
  const [error, setError] = useState('');
  const [mutDelete, pending] = useMutation(mutationDeletePm);

  function submit() {
    setError('');
    mutDelete({
      variables: { corpoRef },
      onCompleted: (res) => {
        const payload = res?.payment?.corpo?.res;
        if (payload?.code !== 200) {
          setError(payload?.message || 'La suppression a échoué.');
          return;
        }
        onDeleted();
      },
      onError: () => setError('La suppression a échoué.'),
    });
  }

  if (!open) {
    return (
      <button
        type="button"
        // Action secondaire et rare : quasi invisible (gris, sans cadre), le rouge n'apparait
        // qu'a l'ouverture, sur le bouton de confirmation.
        className="self-start text-xs text-was-muted/70 hover:text-was-slate hover:underline"
        onClick={onOpen}
      >
        Supprimer le moyen de paiement enregistré
      </button>
    );
  }

  return (
    <div className="flex flex-col mt-2">
      <p className="text-xs leading-tight text-dark mb-2">
        Le moyen de paiement enregistré pour l&apos;abonnement sera détaché et supprimé. Vous pourrez en
        renseigner un nouveau lors d&apos;une prochaine souscription.
      </p>
      {!error || <div className="mb-2 text-red">{error}</div>}
      {pending ? <Loading /> : (
        <div className="flex gap-2">
          <button
            type="button"
            className="flex items-center justify-center px-3 py-2 text-white lg:h-10 bg-red lg:px-4 hover:bg-red/80"
            onClick={submit}
          >
            Confirmer la suppression
          </button>
          <button
            type="button"
            className="flex items-center px-3 py-2 gray"
            onClick={() => { onClose(); setError(''); }}
          >
            Annuler
          </button>
        </div>
      )}
    </div>
  );
}

function LegendContent({
  data, loadQuery, corpoRef, corpoName, isAdmin,
}) {
  const { t: tr, lang } = useTranslation();
  // only one panel open at a time -> at most one Stripe CardElement mounted
  const [panel, setPanel] = useState('');
  const [showInvoices, setShowInvoices] = useState(false);
  const sub = data?.payment?.corpo?.res;
  const plans = data?.payment?.corpo?.plans || [];
  if (!sub) return <Loading />;

  if (!sub.hasSubscription) {
    return (
      <LayoutCard title="Legend +">
        <p className="mb-3 text-sm text-was-slate">
          Aucun abonnement Legend + actif sur votre compte. Souscrivez ci-dessous pour en profiter.
        </p>
        <SubscribeBlock
          corpoRef={corpoRef}
          corpoName={corpoName}
          plans={plans}
          assignedCoupon={sub.assignedCoupon}
          assignedFreeMonths={sub.assignedFreeMonths}
          onSubscribed={() => loadQuery?.({ corpoRef }, { fetchPolicy: 'network-only' })}
        />
        {isAdmin && sub.hasSavedPaymentMethod && (
          <div className="mt-3">
            <DeleteBlock
              corpoRef={corpoRef}
              open={panel === 'delete'}
              onOpen={() => setPanel('delete')}
              onClose={() => setPanel('')}
              onDeleted={() => { setPanel(''); loadQuery?.({ corpoRef }, { fetchPolicy: 'network-only' }); }}
            />
          </div>
        )}
      </LayoutCard>
    );
  }

  const st = STATUS[sub.statusLabel] || STATUS.pending;
  const hasIssue = sub.statusLabel === 'failed';
  const isCanceled = sub.statusLabel === 'canceled';
  const invoices = sub.invoices || [];

  return (
    <LayoutCard title="Legend +">
      {/* Abonnement en cours — carte compacte, même langage visuel que le teaser Legend+ du header
          (FidelityProgramCard) : en-tête rouge WAS teinte réduite + corps crème. Offre + prochain
          prélèvement en avant, le détail reste secondaire. */}
      <div className="overflow-hidden mb-4 rounded-xl border border-red/15">
        <div className="flex gap-3 items-center p-4 bg-red/10">
          <span className="flex justify-center items-center rounded-lg size-11 shrink-0 bg-red/15 text-red">
            <HofPlusIcon className="size-7" />
          </span>
          <div className="flex flex-1 gap-2 items-center">
            <span className="text-lg font-medium text-was-ink">Legend+</span>
            <span className={`px-2 py-0.5 text-[10px] font-medium tracking-wide uppercase rounded-full ${st.chip}`}>
              {st.label}
            </span>
          </div>
          <div className="flex items-baseline gap-1 shrink-0">
            <span className="text-2xl font-medium leading-none text-red">
              {fmtMoney(sub.amount, sub.currency, lang)}
            </span>
            <span className="text-xs text-red/80">
              {sub.interval === 'month' ? tr('corpo:abonnement.offerPerMonth') : sub.interval}
            </span>
          </div>
        </div>

        <div className="p-4 bg-was-cream">
          {hasIssue && (
            <p className="mb-3 text-sm text-red">
              Votre dernier prélèvement n&apos;est pas passé
              {sub.lastIncidentReason ? ` (${sub.lastIncidentReason})` : ''}
              . Changez de moyen de paiement pour régulariser.
            </p>
          )}

          {!isCanceled && sub.nextChargeDate && !sub.cancelAtPeriodEnd && (
            <div className="px-4 py-3 mb-3 rounded-xl border border-was-ink/8 bg-white">
              <span className="block text-[10px] font-medium tracking-wide uppercase text-was-muted">
                {sub.isTrialing ? 'Premier prélèvement' : 'Prochain prélèvement'}
              </span>
              <span className="block mt-1 text-base font-medium text-was-ink">
                {fmtMoney(sub.amount, sub.currency, lang)}
                {' • '}
                {fmtDate(sub.nextChargeDate)}
              </span>
            </div>
          )}
          {sub.cancelAtPeriodEnd && (
            <p className="mb-3 text-sm text-red">Résiliation programmée en fin de période payée.</p>
          )}

          <div className="text-sm text-was-slate">
            {sub.paymentMethod && (
              <div>
                Moyen de paiement :
                {' '}
                <b className="text-was-ink">
                  {sub.paymentMethod.type === 'sepa' ? 'Prélèvement SEPA' : 'Carte'}
                  {sub.paymentMethod.last4 ? ` •••• ${sub.paymentMethod.last4}` : ''}
                </b>
              </div>
            )}
            {sub.coupon && (
              <div>
                Remise :
                {' '}
                <b className="text-green-600">{couponLabel(sub.coupon)}</b>
              </div>
            )}
            {sub.isTrialing && sub.trialEnd && (
              <div>
                <b className="text-green-600">
                  {sub.freeMonths ? `${sub.freeMonths} mois offert${sub.freeMonths > 1 ? 's' : ''}` : 'Période offerte'}
                </b>
                {' — 1er prélèvement le '}
                <b className="text-was-ink">{fmtDate(sub.trialEnd)}</b>
              </div>
            )}
            {sub.commitmentEndDate && (
              <div>
                Fin de l&apos;engagement :
                {' '}
                <b className="text-was-ink">{fmtDate(sub.commitmentEndDate)}</b>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mois de prélèvement — repliés par défaut pour ne pas noyer la carte d'abonnement */}
      <div className="mb-4">
        <button
          type="button"
          className="flex gap-2 items-center pb-1 font-medium text-was-ink hover:text-was-slate"
          onClick={() => setShowInvoices((v) => !v)}
        >
          {showInvoices
            ? tr('corpo:abonnement.invoicesHide')
            : tr('corpo:abonnement.invoicesToggle')}
          <span aria-hidden="true" className={showInvoices ? 'rotate-180' : ''}>▾</span>
        </button>
        {!showInvoices || (invoices.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-1 pr-3">Mois</th>
                  <th className="py-1 pr-3">Date</th>
                  <th className="py-1 pr-3">Montant</th>
                  <th className="py-1 pr-3">Statut</th>
                  <th className="py-1 pr-3">Facture</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-was-ink/8">
                    <td className="py-1 pr-3">{fmtMonth(inv.periodStart || inv.date)}</td>
                    <td className="py-1 pr-3">{fmtDate(inv.date)}</td>
                    <td className="py-1 pr-3">
                      {inv.amount}
                      {' '}
                      {(inv.currency || 'eur').toUpperCase()}
                    </td>
                    <td className="py-1 pr-3">{INVOICE_STATUS[inv.status] || inv.status}</td>
                    <td className="py-1 pr-3">
                      {inv.hostedInvoiceUrl || inv.invoicePdf ? (
                        <a
                          href={inv.hostedInvoiceUrl || inv.invoicePdf}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-red underline"
                        >
                          Voir
                        </a>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="text-sm text-was-slate">Aucun prélèvement pour le moment.</p>)}
      </div>

      {/*
        Changer de moyen de paiement : TOUT LE MONDE si paiement en échec (régularisation),
        sinon ADMIN uniquement (« à tout moment »). Résilier / reprendre : ADMIN uniquement.
      */}
      {['active', 'failed', 'pending'].includes(sub.statusLabel) && (isAdmin || hasIssue) && (
        <div className="flex flex-col gap-2">
          {(isAdmin || hasIssue) && (
            <ChangePaymentBlock
              corpoRef={corpoRef}
              corpoName={corpoName}
              hasIssue={hasIssue}
              open={panel === 'change'}
              onOpen={() => setPanel('change')}
              onClose={() => setPanel('')}
              onChanged={() => { setPanel(''); loadQuery?.({ corpoRef }, { fetchPolicy: 'network-only' }); }}
            />
          )}
          {isAdmin && (sub.cancelAtPeriodEnd ? (
            <ResumeBlock
              corpoRef={corpoRef}
              corpoName={corpoName}
              open={panel === 'resume'}
              onOpen={() => setPanel('resume')}
              onClose={() => setPanel('')}
              onResumed={() => { setPanel(''); loadQuery?.({ corpoRef }, { fetchPolicy: 'network-only' }); }}
            />
          ) : (
            <CancelBlock
              corpoRef={corpoRef}
              commitmentEndDate={sub.commitmentEndDate}
              open={panel === 'cancel'}
              onOpen={() => setPanel('cancel')}
              onClose={() => setPanel('')}
              onCanceled={() => { setPanel(''); loadQuery?.({ corpoRef }, { fetchPolicy: 'network-only' }); }}
            />
          ))}
        </div>
      )}

      {/* Abonnement résilié : re-souscription possible (ouverte à tous, comme la souscription initiale) */}
      {isCanceled && (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-was-slate">
            Votre abonnement Legend + est résilié. Vous pouvez en souscrire un nouveau ci-dessous.
          </p>
          <SubscribeBlock
            corpoRef={corpoRef}
            corpoName={corpoName}
            plans={plans}
            assignedCoupon={sub.assignedCoupon}
            assignedFreeMonths={sub.assignedFreeMonths}
            onSubscribed={() => loadQuery?.({ corpoRef }, { fetchPolicy: 'network-only' })}
          />
          {isAdmin && sub.hasSavedPaymentMethod && (
            <DeleteBlock
              corpoRef={corpoRef}
              open={panel === 'delete'}
              onOpen={() => setPanel('delete')}
              onClose={() => setPanel('')}
              onDeleted={() => { setPanel(''); loadQuery?.({ corpoRef }, { fetchPolicy: 'network-only' }); }}
            />
          )}
        </div>
      )}
    </LayoutCard>
  );
}

const QLContainer = miniQLWS({
  query: querySubscription,
  providerVariables: ({ corpoRef }) => ({ corpoRef }),
  Component: LegendContent,
});

export default function LegendCard({ userCorpoData }) {
  const userData = useZusUserConnectedStore((state) => state.userData);
  const [stripe, setStripe] = useState(null);

  useEffect(() => {
    let mounted = true;
    loadStripe(process.env.NEXT_PUBLIC_STRIPE_API).then((s) => { if (mounted) setStripe(s); });
    return () => { mounted = false; };
  }, []);

  const corpoRef = userData?.corpo?.ref || userCorpoData?.ref;
  const isAdmin = !!userData?.corpoUserConf?.isAdmin;
  if (!corpoRef) return null;

  return (
    <div className="flex items-center justify-center w-full">
      {stripe ? (
        <Elements stripe={stripe}>
          <QLContainer corpoRef={corpoRef} corpoName={userCorpoData?.name} isAdmin={isAdmin} />
        </Elements>
      ) : <Loading />}
    </div>
  );
}
